import { TimeScheduleCalendar } from '@spiaggeit/spit-datepicker'
import { Button, Dialog } from '@spiaggeit/spit-ui'
import { DateTime } from 'luxon'
import { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useAppDispatch, useAppSelector } from '@/hooks/store'
import { BoughServiceByTimeSchedule } from '@/models/quote'
import { cartSlice } from '@/store/cartSlice'
import { serviceUnavailableDaysSelector } from '@/store/cartSlice/selectors/serviceUnavailableDaysSelector'
import {
  dayDifferenceSelector,
  insertPeriodSlice,
} from '@/store/insertPeriodSlice'
import { AddedServices, timeScheduleSlice } from '@/store/timeScheduleSlice'
import { dialogTotalSelector } from '@/store/timeScheduleSlice/selectors/dialogTotalSelector'
import { globalAvailabilitySelector } from '@/store/timeScheduleSlice/selectors/globalAvailabilitySelector'
import { groupAvailabilitySelector } from '@/store/timeScheduleSlice/selectors/groupAvailabilitySelector'
import { serviceAmountSelector } from '@/store/timeScheduleSlice/selectors/serviceAmountSelector'
import { visibleTimeSchedulesSelector } from '@/store/timeScheduleSlice/selectors/visibleTimeSchedulesSelector'
import { SERVICE_LABEL_LIMIT } from '@/utils/constants'
import { convertLocalTimeToUTCTime } from '@/utils/dateUtils'
import { getServiceDays } from '@/utils/getServiceDays'
import { getServiceSingleLimit } from '@/utils/getServiceSingleLimit'
import { priceFormatter } from '@/utils/price'
import { getTimeScheduleKey } from '@/utils/timeSchedules'
import { TimeScheduleQuantityPicker } from './TimeScheduleQuantityPicker'

type Props = {
  onClose: VoidFunction
}

export function TimeScheduleDialog(props: Props) {
  const dispatch = useAppDispatch()
  const { i18n, t } = useTranslation()

  const addedServices = useAppSelector(
    timeScheduleSlice.selectors.addedServices
  )
  const cartReservations = useAppSelector(cartSlice.selectors.cartReservations)
  const dayDifference = useAppSelector(dayDifferenceSelector)
  const globalAvailability = useAppSelector(globalAvailabilitySelector)
  const groupAvailability = useAppSelector(groupAvailabilitySelector)
  const period = useAppSelector(insertPeriodSlice.selectors.period)
  const service = useAppSelector(timeScheduleSlice.selectors.service)
  const serviceAmount = useAppSelector(serviceAmountSelector)
  const selectedDays = useAppSelector(timeScheduleSlice.selectors.selectedDays)
  const total = useAppSelector(dialogTotalSelector)
  const unavailableDays = useAppSelector((state) =>
    serviceUnavailableDaysSelector(state, service)
  )

  const visibleTimeSchedules = useAppSelector(visibleTimeSchedulesSelector)

  const serviceDays = useMemo(() => {
    return getServiceDays(cartReservations, service)
  }, [cartReservations, service])

  useEffect(() => {
    const selectedDays = serviceDays.map((time) => {
      const isoDate = DateTime.fromSeconds(time, {
        zone: 'utc',
      }).toISODate()

      return DateTime.fromISO(isoDate).toJSDate()
        .getTime()
    })
    dispatch(timeScheduleSlice.actions.setSelectedDays(selectedDays))
  }, [])

  useEffect(() => {
    const addedServicesKeys = Object.keys(addedServices)
    if (addedServicesKeys.length === 0) {
      return
    }

    const visibleTimeSchedulesKeys =
      visibleTimeSchedules.map(getTimeScheduleKey)

    const newServices = addedServicesKeys.reduce<AddedServices>((acc, key) => {
      if (visibleTimeSchedulesKeys.includes(key)) {
        acc[key] = {
          ...addedServices[key],
        }
      }
      return acc
    }, {})

    if (Object.keys(newServices).length !== addedServicesKeys.length) {
      dispatch(timeScheduleSlice.actions.setAddedServices(newServices))
    }
  }, [addedServices, visibleTimeSchedules, selectedDays])

  if (period === null || service === null) {
    return null
  }

  function handleAdd() {
    if (service === null) {
      return
    }

    const boughtTimeSchedules = Object.values(
      addedServices
    ).reduce<BoughServiceByTimeSchedule>((acc, item) => {
      acc[getTimeScheduleKey(item.timeSchedule)] = {
        bought: item.bought,
        days: selectedDays.map(convertLocalTimeToUTCTime),
        serviceId: service.serviceId,
        serviceName: service.name,
        timeSchedule: item.timeSchedule,
      }
      return acc
    }, {})

    dispatch(
      cartSlice.actions.addTimeScheduleToCart({
        boughtTimeSchedules,
        reservation: {
          spotName: null,
          spotType: null,
        },
        service,
      })
    )

    props.onClose()
  }

  const max = getServiceSingleLimit({
    // availableQuantity: service.availableQuantity,
    dayDifference,
    service,
  })

  const minMaxLabel = (() => {
    const label = [
      ...(service.minimumQuantity > 0
        ? [
            `${t('timeSchedulesDialog.min', { value: service.minimumQuantity })}`,
          ]
        : []),
      ...(service.hasLimit && max < SERVICE_LABEL_LIMIT
        ? [`${t('timeSchedulesDialog.max', { value: max })}`]
        : []),
    ].join(' · ')

    return label ? ` · ${label}` : null
  })()

  return (
    <Dialog
      className="lg:max-w-[40rem]"
      isOpen={true}
      mobileMode="fullscreen"
      setIsOpen={(isOpen: boolean) => {
        if (!isOpen) {
          props.onClose()
        }
      }}
      title={service.name}
      trigger={null}
    >
      <div className="flex grow flex-col overflow-y-auto border-t lg:flex-row">
        {dayDifference > 1 ? (
          <div className="p-4 lg:border-r lg:py-2">
            <div className="font-bold">
              {t('timeSchedulesDialog.datepickerTitle')}
            </div>

            <TimeScheduleCalendar
              lang={i18n.language}
              maxDate={DateTime.fromISO(period.end).toJSDate()}
              minDate={DateTime.fromISO(period.start).toJSDate()}
              onDateSelect={(time) => {
                dispatch(timeScheduleSlice.actions.daySelected(time))
              }}
              selectedDates={selectedDays}
              unavailableDays={unavailableDays}
            />
          </div>
        ) : null}

        <div className="grow space-y-4 p-4 lg:max-h-96 lg:overflow-y-auto">
          <div className="font-bold">
            {t('timeSchedulesDialog.timeSelect')}
            {minMaxLabel ? (
              <span className="font-normal">{minMaxLabel}</span>
            ) : null}
          </div>

          {visibleTimeSchedules.map((timeSchedule, index) => (
            <TimeScheduleQuantityPicker
              availability={{
                global: globalAvailability,
                group: groupAvailability,
                service: getServiceSingleLimit({
                  dayDifference: selectedDays.length,
                  service,
                }),
              }}
              dayDifference={dayDifference}
              key={index}
              max={max}
              service={service}
              serviceAmount={serviceAmount}
              timeSchedule={timeSchedule}
            />
          ))}

          {selectedDays.length > 0 && visibleTimeSchedules.length === 0 ? (
            <div>{t('timeSchedulesDialog.noScheduleAvailable')}</div>
          ) : null}

          {selectedDays.length === 0 ? (
            <div>{t('timeSchedulesDialog.noDaySelected')}</div>
          ) : null}
        </div>
      </div>

      <div className="flex items-center justify-between gap-2 border-t p-4">
        <div className="min-w-0">
          <div className="text-sm">{t('timeSchedulesDialog.total')}:</div>
          <div className="font-bold">{priceFormatter.format(total)}</div>
        </div>

        <div className="flex items-center gap-2">
          <Button
            className="rounded"
            onClick={props.onClose}
            type="button"
            variant="outline"
          >
            {t('timeSchedulesDialog.cancel')}
          </Button>

          <Button
            className="rounded"
            disabled={
              selectedDays.length === 0 ||
              serviceAmount < service.minimumQuantity
            }
            onClick={handleAdd}
            type="button"
          >
            {t('timeSchedulesDialog.add')}
          </Button>
        </div>
      </div>
    </Dialog>
  )
}
